import React, { useEffect, useState, useCallback, useMemo } from "react";

// Creative Tim
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import Pagination from "@material-ui/lab/Pagination";
import NewsTranslations from "../components/NewsTranslations";
import NotInvestmentAdviceBanner from "components/Legal/NotInvestmentAdviceBanner.jsx";

// Material UI (v4)
import {
    Typography,
    Tabs,
    Tab,
    Divider,
    TextField,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Tooltip,
} from "@material-ui/core";

// MUI Icons (v4)
import WhatshotIcon from "@material-ui/icons/Whatshot";

// API
import {
    getNewsByDate,
    getNewsDetail,
    getStats,
    getAlertsBySymbolAndDate,
    getAlertSummaryBySymbolsAndDate,
} from "../services/api";

// Custom Components
import NewsList from "../components/NewsList";
import NewsAnalysisCard from "../components/NewsAnalysisCard";
import SignalsListCard from "../components/SignalsListCard";
import KpiCard from "../components/KpiCard";
import SentimentGaugeCard from "../components/SentimentGaugeCard";
import SymbolChartCard from "../components/SymbolChartCard";

import {
    getItemLookupSymbol,
    getNewsSymbol,
    getNewsLookupSymbol,
    getLookupSymbol,
    isValidSymbol,
    normalizeSymbol,
} from "../utils/symbols";

const STORAGE_KEY = "aktiennews.dashboard.filters.v1";

function safeSavePrefs(prefs) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
        // ignore
    }
}

function safeClearPrefs() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

function renderText(value, fallback = "") {
    if (value === null || value === undefined) return fallback;

    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);

    if (Array.isArray(value)) {
        return value.map((v) => renderText(v)).filter(Boolean).join(", ");
    }

    if (typeof value === "object") {
        return (
            value.companyName ||
            value.CompanyName ||
            value.name ||
            value.Name ||
            value.symbol ||
            value.Symbol ||
            value.title ||
            value.Title ||
            value.value ||
            value.label ||
            value.rationale ||
            value.rationale_deutsch ||
            value.rationale_englisch ||
            Object.values(value).find((v) => typeof v === "string" && v.trim()) ||
            fallback
        );
    }

    return fallback;
}

function normalizePrice(value) {
    if (value == null || value === "") return null;
    const parsed = Number(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeDateOnly(value) {
    if (!value) return null;

    const str = String(value).trim();

    const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
        return match[1];
    }

    const parsed = new Date(str);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
}

function safeParseAnalysisJson(maybeJson) {
    if (!maybeJson) return null;

    try {
        let text = String(maybeJson).trim();

        if (text.startsWith("```")) {
            const lines = text.split(/\r?\n/);
            lines.shift();
            if (lines.length && lines[lines.length - 1].trim().startsWith("```")) {
                lines.pop();
            }
            text = lines.join("\n").trim();
        }

        const first = text.indexOf("{");
        const last = text.lastIndexOf("}");
        if (first !== -1 && last !== -1 && last > first) {
            text = text.substring(first, last + 1);
        }

        return JSON.parse(text);
    } catch {
        return null;
    }
}

function getFirstArrayItem(value) {
    return Array.isArray(value) ? value[0] : null;
}

function getDetailPrimaryLookupSymbol(detail) {
    const newsSymbol = getNewsLookupSymbol(detail?.news);
    if (newsSymbol) {
        return newsSymbol;
    }

    const parsed = safeParseAnalysisJson(detail?.analysis?.json);
    const parsedTickers = Array.isArray(parsed?.tickers) ? parsed.tickers : [];

    const firstTicker = getFirstArrayItem(parsedTickers);
    const raw = firstTicker?.symbol || firstTicker || null;

    return getLookupSymbol(raw);
}

function getDetailPrimaryDisplaySymbol(detail) {
    const direct = getNewsSymbol(detail?.news);
    if (direct) return direct;

    const parsed = safeParseAnalysisJson(detail?.analysis?.json);
    const parsedTickers = Array.isArray(parsed?.tickers) ? parsed.tickers : [];

    const firstTicker = getFirstArrayItem(parsedTickers);
    return normalizeSymbol(firstTicker?.symbol || firstTicker || null);
}

function getDetailEventDate(detail, fallbackDate) {
    const publishedAt = detail?.news?.publishedAt;

    if (!publishedAt) return fallbackDate;

    const parsed = new Date(String(publishedAt).replace(" ", "T") + "Z");
    if (Number.isNaN(parsed.getTime())) return fallbackDate;

    return parsed.toISOString().substring(0, 10);
}

function mapAlertToSignal(a) {
    return {
        id: a.Id ?? a.id,
        symbol: normalizeSymbol(a.Symbol ?? a.symbol),
        companyName: renderText(a.CompanyName ?? a.companyName, null),
        direction: (a.Direction ?? a.direction ?? "").toUpperCase(),
        price: normalizePrice(a.Price ?? a.price),
        eventTime: a["Event time"] ?? a.EventTime ?? a.eventTime ?? null,
        eventDate: a.EventDate ?? a.eventDate ?? null,
        raw: a.Raw ?? a.raw ?? null,
        currency: a.Currency ?? a.currency ?? null,
        volume: a.Volume ?? a.volume ?? null,
    };
}

function mapDetailSignalToDisplaySignal(s, fallbackCompanyName = null, fallbackSymbol = null) {
    const directionRaw = s.direction || s.action || "";
    const directionUpper = String(directionRaw).toUpperCase();

    let direction = directionUpper;
    if (directionUpper === "BUY") direction = "LONG";
    if (directionUpper === "SELL") direction = "SHORT";
    if (directionUpper === "HOLD") direction = "HOLD";

    const resolvedSymbol = normalizeSymbol(
        s.symbol ||
        s.ticker ||
        fallbackSymbol ||
        null
    );

    return {
        ...s,
        id: s.id,
        symbol: resolvedSymbol,
        companyName: renderText(s.companyName || fallbackCompanyName, null),
        direction,
        price: normalizePrice(s.price ?? s.priceAtSignal),
        eventTime: s.eventTime || s.createdAt || null,
        eventDate: s.eventDate || null,
    };
}

export default function AktienNewsDashboard() {
    const todayStr = useMemo(() => new Date().toISOString().substring(0, 10), []);

    function clampStartDate(startStr) {
        if (!startStr) return todayStr;
        return startStr > todayStr ? todayStr : startStr;
    }

    function clampEndDate(startStr, endStr) {
        const start = new Date(startStr + "T00:00:00Z");
        const today = new Date(todayStr + "T00:00:00Z");

        if (!endStr) return startStr;

        let end = new Date(endStr + "T00:00:00Z");

        if (end > today) end = today;
        if (end < start) end = start;

        const maxEnd = new Date(start);
        maxEnd.setUTCDate(maxEnd.getUTCDate() + 6);
        if (end > maxEnd) end = maxEnd;

        return end.toISOString().substring(0, 10);
    }

    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [globalStats, setGlobalStats] = useState(null);

    const [alertsForDay, setAlertsForDay] = useState([]);
    const [alertSummaryMap, setAlertSummaryMap] = useState({});

    const [activeTab, setActiveTab] = useState(0);

    const [selectedDate, setSelectedDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);

    const [filterBuy, setFilterBuy] = useState(false);
    const [filterSell, setFilterSell] = useState(false);
    const [filterHold, setFilterHold] = useState(false);

    const [qDraft, setQDraft] = useState("");
    const [q, setQ] = useState("");

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [newsList, setNewsList] = useState([]);
    const PAGE_SIZE = 13;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMsg, setDialogMsg] = useState("");

    const openDialog = (msg) => {
        setDialogMsg(renderText(msg));
        setDialogOpen(true);
    };

    const resetAllFilters = useCallback(() => {
        safeClearPrefs();

        setSelectedDate(todayStr);
        setEndDate(todayStr);

        setFilterBuy(false);
        setFilterSell(false);
        setFilterHold(false);

        setQ("");
        setQDraft("");

        setPage(1);
        setSelectedId(null);
        setDetail(null);
        setAlertsForDay([]);
        setAlertSummaryMap({});
    }, [todayStr]);

    const handleSelectNews = useCallback((id) => {
        if (!id) return;

        setSelectedId((currentId) => {
            if (currentId === id) {
                return currentId;
            }

            setDetail(null);
            setAlertsForDay([]);
            return id;
        });
    }, []);

    useEffect(() => {
        resetAllFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        safeSavePrefs({
            selectedDate,
            endDate,
            filterBuy,
            filterSell,
            filterHold,
            q,
            qDraft,
        });
    }, [selectedDate, endDate, filterBuy, filterSell, filterHold, q, qDraft]);

    const handleSelectedDateChange = (value) => {
        const start = clampStartDate(value);
        setSelectedDate(start);

        const clampedEnd = clampEndDate(start, endDate || start);
        setEndDate(clampedEnd);
    };

    const handleEndDateChange = (value) => {
        const clamped = clampEndDate(selectedDate, value);
        setEndDate(clamped);
    };

    const commitSearch = () => {
        const trimmed = (qDraft || "").trim();

        if (trimmed.length > 0 && trimmed.length < 3) {
            openDialog("Bitte mindestens 3 Zeichen für die Suche eingeben.");
            return;
        }

        setQ(trimmed);
        setPage(1);
    };

    const loadNews = useCallback(async () => {
        const actions = [];
        if (filterBuy) actions.push("buy");
        if (filterSell) actions.push("sell");
        if (filterHold) actions.push("hold");

        const endParam = endDate && endDate !== selectedDate ? endDate : null;

        const res = await getNewsByDate(
            selectedDate,
            endParam,
            actions,
            q,
            page,
            PAGE_SIZE
        );

        const items = res.items || [];
        setNewsList(items);
        setTotalPages(res.totalPages || 1);

        setSelectedId((currentSelectedId) => {
            if (items.length === 0) return null;

            const stillExists =
                currentSelectedId && items.some((x) => x.id === currentSelectedId);

            return stillExists ? currentSelectedId : items[0].id;
        });
    }, [
        selectedDate,
        endDate,
        filterBuy,
        filterSell,
        filterHold,
        q,
        page,
    ]);

    useEffect(() => {
        setPage(1);
    }, [selectedDate, endDate, filterBuy, filterSell, filterHold]);

    useEffect(() => {
        loadNews();
    }, [loadNews]);

    useEffect(() => {
        let mounted = true;

        const loadStats = async () => {
            const stats = await getStats();
            if (mounted) setGlobalStats(stats || null);
        };

        loadStats();
        const interval = setInterval(loadStats, 60000);

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        if (!selectedId) {
            setDetail(null);
            setAlertsForDay([]);
            return;
        }

        let mounted = true;

        (async () => {
            try {
                const d = await getNewsDetail(selectedId);
                if (!mounted) return;

                setDetail(d || null);

                const lookupSymbol = getDetailPrimaryLookupSymbol(d);
                const alertDate = getDetailEventDate(d, selectedDate);

                console.log("selectedId", selectedId);
                console.log("primary lookupSymbol", lookupSymbol);
                console.log("alertDate", alertDate);

                if (!lookupSymbol || !isValidSymbol(lookupSymbol)) {
                    setAlertsForDay([]);
                    return;
                }

                const res = await getAlertsBySymbolAndDate(lookupSymbol, alertDate);
                if (!mounted) return;

                const items = Array.isArray(res) ? res : res?.items || [];

                const filteredDayItems = items.filter((x) => {
                    const rawEventDate = x.EventDate ?? x.eventDate ?? null;
                    const normalizedEventDate = normalizeDateOnly(rawEventDate);
                    return !normalizedEventDate || normalizedEventDate === alertDate;
                });

                console.log("alerts raw items", items);
                console.log(
                    "alerts filtered",
                    filteredDayItems.map((x) => ({
                        id: x.Id ?? x.id,
                        rawEventDate: x.EventDate ?? x.eventDate ?? null,
                        normalizedEventDate: normalizeDateOnly(x.EventDate ?? x.eventDate ?? null),
                    }))
                );

                setAlertsForDay(filteredDayItems);
            } catch (err) {
                console.error("detail/alerts load failed", err);
                if (mounted) {
                    setDetail(null);
                    setAlertsForDay([]);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [selectedId, selectedDate]);

    useEffect(() => {
        let mounted = true;

        const loadAlertSummary = async () => {
            try {
                const symbols = [
                    ...new Set(
                        newsList
                            .map((item) => getItemLookupSymbol(item))
                            .filter((s) => isValidSymbol(s))
                    ),
                ];

                if (symbols.length === 0) {
                    if (mounted) setAlertSummaryMap({});
                    return;
                }

                const res = await getAlertSummaryBySymbolsAndDate(symbols, selectedDate);
                if (!mounted) return;

                const items = Array.isArray(res) ? res : res?.items || [];

                const map = items.reduce((acc, item) => {
                    const key = getLookupSymbol(item?.symbol);
                    if (key) {
                        acc[key] = item;
                    }
                    return acc;
                }, {});

                setAlertSummaryMap(map);
            } catch {
                if (mounted) setAlertSummaryMap({});
            }
        };

        loadAlertSummary();

        return () => {
            mounted = false;
        };
    }, [newsList, selectedDate]);

    useEffect(() => {
        const interval = setInterval(() => {
            loadNews();
        }, 60000);

        return () => clearInterval(interval);
    }, [loadNews]);

    const selectedNewsSymbol = useMemo(
        () => renderText(getDetailPrimaryDisplaySymbol(detail), null),
        [detail]
    );

    const selectedNewsLookupSymbol = useMemo(
        () => getDetailPrimaryLookupSymbol(detail),
        [detail]
    );

    const companyNameForHeader = useMemo(() => {
        const alertWithCompanyName = alertsForDay.find(
            (a) => a.CompanyName || a.companyName
        );

        const value =
            alertWithCompanyName?.CompanyName ||
            alertWithCompanyName?.companyName ||
            detail?.news?.companyName ||
            detail?.news?.CompanyName ||
            alertSummaryMap[selectedNewsLookupSymbol]?.companyName ||
            alertSummaryMap[selectedNewsLookupSymbol]?.CompanyName ||
            null;

        return renderText(value, null);
    }, [alertsForDay, detail, alertSummaryMap, selectedNewsLookupSymbol]);

    const daySignals = useMemo(() => {
        if (alertsForDay.length > 0) {
            return alertsForDay
                .map(mapAlertToSignal)
                .filter((s) => isValidSymbol(s.symbol));
        }

        return (detail?.signals || [])
            .map((s) =>
                mapDetailSignalToDisplaySignal(
                    s,
                    renderText(detail?.news?.companyName || detail?.news?.CompanyName, null),
                    selectedNewsLookupSymbol
                )
            )
            .filter((s) => isValidSymbol(s.symbol));
    }, [alertsForDay, detail, selectedNewsLookupSymbol]);

    const kpiStats = useMemo(() => {
        const longs = daySignals.filter((s) => s.direction === "LONG");
        const shorts = daySignals.filter((s) => s.direction === "SHORT");
        const prices = daySignals
            .map((s) => s.price)
            .filter((p) => p != null && Number.isFinite(Number(p)));

        const avgPrice =
            prices.length > 0
                ? prices.reduce((sum, val) => sum + Number(val), 0) / prices.length
                : null;

        const lastSignal =
            daySignals.length > 0
                ? [...daySignals].sort((a, b) => {
                    const da = new Date(a.eventTime || a.eventDate || 0).getTime();
                    const db = new Date(b.eventTime || b.eventDate || 0).getTime();
                    return db - da;
                })[0]
                : null;

        return {
            totalNews: detail?.news ? 1 : 0,
            totalSignals: daySignals.length,
            longSignals: longs.length,
            shortSignals: shorts.length,
            avgPrice,
            lastPrice: lastSignal?.price ?? null,
            companyName: renderText(companyNameForHeader, null),
            symbol: renderText(selectedNewsSymbol, "–"),
        };
    }, [daySignals, detail, companyNameForHeader, selectedNewsSymbol]);

    const headerTextStyle = { color: "#fff", fontWeight: 600 };

    return (
        <GridContainer>
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Hinweis</DialogTitle>
                <DialogContent>
                    <Typography>{renderText(dialogMsg)}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        color="primary"
                        variant="contained"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <GridItem xs={12} md={4}>
                <Card>
                    <CardHeader color="primary">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 16,
                                flexWrap: "wrap",
                                width: "100%",
                            }}
                        >
                            <Typography style={headerTextStyle}>Headlines vom</Typography>

                            <TextField
                                type="date"
                                size="small"
                                value={selectedDate}
                                onChange={(e) => handleSelectedDateChange(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ style: headerTextStyle }}
                            />

                            <Typography style={headerTextStyle}>bis</Typography>

                            <TextField
                                type="date"
                                size="small"
                                value={endDate}
                                onChange={(e) => handleEndDateChange(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ style: headerTextStyle }}
                            />

                            <TextField
                                size="small"
                                value={qDraft}
                                onChange={(e) => setQDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") commitSearch();
                                }}
                                placeholder="Suche (Enter, min. 3 Zeichen)"
                                InputProps={{ style: headerTextStyle }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterBuy}
                                        onChange={(e) => setFilterBuy(e.target.checked)}
                                        style={{ color: "#2ecc71" }}
                                    />
                                }
                                label="Buy"
                                style={{ margin: 0, color: "#fff" }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterSell}
                                        onChange={(e) => setFilterSell(e.target.checked)}
                                        style={{ color: "#e74c3c" }}
                                    />
                                }
                                label="Sell"
                                style={{ margin: 0, color: "#fff" }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filterHold}
                                        onChange={(e) => setFilterHold(e.target.checked)}
                                        style={{ color: "darkblue" }}
                                    />
                                }
                                label="Hold"
                                style={{ margin: 0, color: "#fff" }}
                            />

                            <Tooltip title="Reset: Heute + alle Filter zurücksetzen">
                                <IconButton
                                    onClick={resetAllFilters}
                                    size="small"
                                    style={{ color: "#e74c3c" }}
                                    aria-label="reset-filters"
                                >
                                    <WhatshotIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </CardHeader>

                    <CardBody style={{ overflowY: "auto", maxHeight: "80vh" }}>
                        <NewsList
                            items={newsList}
                            selectedId={selectedId}
                            onSelect={handleSelectNews}
                            alertSummaryMap={alertSummaryMap}
                        />

                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            color="primary"
                            size="small"
                            style={{ marginTop: 12 }}
                        />
                    </CardBody>
                </Card>
            </GridItem>

            <GridItem xs={12} md={8}>
                <Card>
                    <CardHeader color="info">
                        <Typography style={headerTextStyle}>
                            Analyse
                            {companyNameForHeader ? ` – ${renderText(companyNameForHeader)}` : ""}
                        </Typography>
                    </CardHeader>

                    <CardBody>
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => setActiveTab(v)}
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            <Tab label="Analyse" />
                            <Tab label="Signale" />
                            <Tab label="KPIs" />
                            <Tab label="Chart" />
                        </Tabs>

                        <Divider style={{ margin: "12px 0" }} />

                        {activeTab === 0 && detail && (
                            <NewsAnalysisCard
                                item={{
                                    ...detail.news,
                                    analysis: detail.analysis,
                                }}
                            />
                        )}

                        {activeTab === 1 && detail && (
                            <SignalsListCard
                                signals={daySignals}
                                companyName={renderText(companyNameForHeader, null)}
                            />
                        )}

                        {activeTab === 2 && detail && <KpiCard stats={kpiStats} />}

                        {activeTab === 3 && detail && (
                            <SymbolChartCard signals={daySignals} />
                        )}
                        {activeTab === 4 && detail && (
                            <NewsTranslations
                                translationsJson={detail.news?.translationsJson}
                            />
                        )}
                        {!detail && (
                            <Typography variant="body2">
                                Bitte eine News auswählen.
                            </Typography>
                        )}
                    </CardBody>
                </Card>

                <SentimentGaugeCard stats={globalStats} />
            </GridItem>
        </GridContainer>
    );
}